import type { CvParseQuality, ParsedCvData } from '@tabbio/types';
import { api } from './api';
import { clearPostAuthTarget, readPostAuthTargetAsync } from './auth-handoff';
import {
  clearStoredResumeHandoff,
  normalizeResumeHandoffToken,
} from './resume-handoff-storage';

export {
  RESUME_HANDOFF_MAX_AGE_MS,
  normalizeResumeHandoffToken,
  parseStoredResumeHandoff,
  readResumeHandoffToken,
  readResumeHandoffTokenAsync,
  readStoredResumeHandoffAsync,
  saveResumeHandoffToken,
  type StoredResumeHandoffRead,
  type StoredResumeHandoffStaleReason,
} from './resume-handoff-storage';

const CLAIM_CACHE_TTL_MS = 30_000;
/**
 * Parse outcomes the server reports as recoverable (incident 2026-09-02): the
 * handoff is kept until its normal expiry, nothing was written, and a retry
 * after the provider recovers can succeed. The client must keep the token.
 */
const RETRYABLE_HANDOFF_ERROR_CODES = new Set([
  'CV_PARSE_INCOMPLETE',
  'CV_PROVIDER_UNAVAILABLE',
]);
/**
 * The handoff itself is gone or was never ours: no retry can succeed and the
 * user did nothing wrong in this session, so these are never shown as a red
 * error. The server tells them apart (resume-handoffs.service.ts):
 * NOT_FOUND (no such row: consumed long ago, or a token from another
 * environment), EXPIRED (past its TTL), ALREADY_CLAIMED (another account
 * imported it), INVALID (malformed).
 */
export const TERMINAL_RESUME_HANDOFF_ERROR_CODES = [
  'HANDOFF_NOT_FOUND',
  'HANDOFF_EXPIRED',
  'HANDOFF_ALREADY_CLAIMED',
  'HANDOFF_INVALID',
] as const;
export type TerminalResumeHandoffErrorCode =
  (typeof TERMINAL_RESUME_HANDOFF_ERROR_CODES)[number];
const TERMINAL_HANDOFF_ERROR_CODE_SET = new Set<string>(
  TERMINAL_RESUME_HANDOFF_ERROR_CODES,
);
const EXPECTED_HANDOFF_ERROR_CODES = new Set([
  ...TERMINAL_RESUME_HANDOFF_ERROR_CODES,
  'MISSING_FILE',
  'REQUEST_ABORTED',
  'UNREADABLE_FILE',
  'UNSUPPORTED_FILE_TYPE',
  'VISION_EXTRACT_FAILED',
  'WORD_EXTRACT_FAILED',
  ...RETRYABLE_HANDOFF_ERROR_CODES,
]);
const UNREADABLE_HANDOFF_ERROR_CODES = new Set([
  'UNREADABLE_FILE',
  'UNSUPPORTED_FILE_TYPE',
  'VISION_EXTRACT_FAILED',
  'WORD_EXTRACT_FAILED',
]);
const LEGACY_HANDOFF_PARSE_FAILURE_MESSAGES = [
  'could not extract meaningful text from file',
  'could not extract content from file',
  'could not extract text from word document',
  'unsupported file type',
];
const claimCache = new Map<
  string,
  {
    expiresAt: number;
    promise: Promise<ResumeHandoffClaimResult>;
    settled: boolean;
  }
>();

export type ResumeHandoffClaimResult = {
  parsed: ParsedCvData;
  /** How the resume was parsed; absent from pre-incident backends. */
  quality?: CvParseQuality;
  importEvidenceId?: string;
  source: string;
  /**
   * Catalog skill a public share page attached to the handoff
   * (skills.tabbio.com funnel); onboarding auto-applies it as
   * `styleSegmentId: skill:<skillId>`.
   */
  skillId?: string | null;
  /**
   * Set when the handoff came from the public CV Health funnel
   * (cvhealth.tabbio.com). Informational: the onboarding CV Health step runs
   * its own review on the imported CV.
   */
  cvHealthToken?: string | null;
};

export class ResumeHandoffClientError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly meta?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ResumeHandoffClientError';
  }
}

/**
 * The handoff is still claimable: the server refused to consume it because
 * the parse was incomplete or the AI provider was unavailable. Callers keep
 * the stored token so the import is retried instead of lost.
 */
export function isRetryableResumeHandoffClientError(error: unknown) {
  return (
    error instanceof ResumeHandoffClientError &&
    RETRYABLE_HANDOFF_ERROR_CODES.has(error.code)
  );
}

/** The terminal code behind a failed claim, or null for anything else. */
export function getTerminalResumeHandoffErrorCode(
  error: unknown,
): TerminalResumeHandoffErrorCode | null {
  return error instanceof ResumeHandoffClientError &&
    TERMINAL_HANDOFF_ERROR_CODE_SET.has(error.code)
    ? (error.code as TerminalResumeHandoffErrorCode)
    : null;
}

export function isTerminalResumeHandoffClientError(error: unknown) {
  return getTerminalResumeHandoffErrorCode(error) !== null;
}

/** The exact extracted text the server offered back for a manual review. */
export function getResumeHandoffExtractedText(error: unknown) {
  if (!(error instanceof ResumeHandoffClientError)) return null;
  const text = error.meta?.extractedText;
  return typeof text === 'string' && text.trim() ? text : null;
}

export function isExpectedResumeHandoffClientError(error: unknown) {
  if (!(error instanceof ResumeHandoffClientError)) {
    return false;
  }

  if (EXPECTED_HANDOFF_ERROR_CODES.has(error.code)) {
    return true;
  }

  const message = error.message.toLowerCase();
  return (
    error.code === 'PARSE_FAILED' &&
    LEGACY_HANDOFF_PARSE_FAILURE_MESSAGES.some((expectedMessage) =>
      message.includes(expectedMessage),
    )
  );
}

export function isUnreadableResumeHandoffClientError(error: unknown) {
  if (!(error instanceof ResumeHandoffClientError)) {
    return false;
  }

  if (UNREADABLE_HANDOFF_ERROR_CODES.has(error.code)) {
    return true;
  }

  const message = error.message.toLowerCase();
  return (
    error.code === 'PARSE_FAILED' &&
    LEGACY_HANDOFF_PARSE_FAILURE_MESSAGES.some((expectedMessage) =>
      message.includes(expectedMessage),
    )
  );
}

/** Forget the pending token (storage and the in-flight claim cache). */
export async function clearResumeHandoffToken() {
  claimCache.clear();
  await clearStoredResumeHandoff();
}

/**
 * The user chose another way in ("Build my CV manually", "Hiring?") while a
 * website upload was pending: forget the upload, and the `resume-import`
 * return it would have triggered after onboarding. Any other post-auth target
 * (a job they applied to, a talent-network join, a message thread) is
 * unrelated to the CV import and is left alone. Shared by native and web so
 * the two cannot drift again.
 */
export async function abandonPendingResumeImport() {
  const target = await readPostAuthTargetAsync();
  if (target === 'resume-import') {
    clearPostAuthTarget();
  }
  await clearResumeHandoffToken();
}

async function requestResumeHandoffClaim(normalized: string) {
  const response = await api.post<ResumeHandoffClaimResult>(
    `/api/resume-handoffs/${encodeURIComponent(normalized)}/claim`,
    {},
    { requiresAuth: true, retryOnUnauthorized: true },
  );

  if (response.error) {
    throw new ResumeHandoffClientError(
      response.error.code,
      response.error.message,
      (response.meta as Record<string, unknown> | null) ?? undefined,
    );
  }

  if (!response.data?.parsed) {
    throw new ResumeHandoffClientError(
      'HANDOFF_PARSE_EMPTY',
      'Could not process your resume. Please upload it again.',
    );
  }

  return response.data;
}

export async function claimResumeHandoff(token: string) {
  const normalized = normalizeResumeHandoffToken(token);
  if (!normalized) {
    throw new ResumeHandoffClientError(
      'HANDOFF_INVALID',
      'This resume handoff is invalid or expired.',
    );
  }

  const now = Date.now();
  const cached = claimCache.get(normalized);
  if (cached && (!cached.settled || cached.expiresAt > now)) {
    return cached.promise;
  }
  if (cached) {
    claimCache.delete(normalized);
  }

  const promise = requestResumeHandoffClaim(normalized);
  claimCache.set(normalized, {
    promise,
    expiresAt: Number.POSITIVE_INFINITY,
    settled: false,
  });

  void promise
    .then(() => {
      const cachedClaim = claimCache.get(normalized);
      if (cachedClaim?.promise === promise) {
        cachedClaim.settled = true;
        cachedClaim.expiresAt = Date.now() + CLAIM_CACHE_TTL_MS;
      }
    })
    .catch(() => {
      if (claimCache.get(normalized)?.promise === promise) {
        claimCache.delete(normalized);
      }
    });

  return promise;
}
